/**
 * Global constants. Anything an admin may want to change at runtime does NOT belong here;
 * it belongs in configuration (`@veriverse/config`) or the database.
 */

/**
 * Bump on every breaking change to realtime messages or room state.
 * Clients with a different version are rejected with `CLIENT_OUTDATED` and asked to reload.
 */
export const PROTOCOL_VERSION = 5;

/** Pixel size of one tile. 1 tile = 1 m² (ADR-0005). Every art asset depends on this. */
export const TILE_SIZE = 32;

/** Character sprite frame: one tile wide, one and a half tiles tall. */
export const CHARACTER_FRAME = { width: 32, height: 48 } as const;

/** Key of the single world (`worlds.key`); also names its Redis keys. */
export const WORLD_KEY = 'office';

export const ROOM_NAMES = {
  office: 'office',
} as const;

/** Close codes / error codes sent to clients. Clients map these to localized messages. */
export const ERROR_CODES = {
  clientOutdated: 'CLIENT_OUTDATED',
  unauthorized: 'UNAUTHORIZED',
  sessionReplaced: 'SESSION_REPLACED',
  kicked: 'KICKED',
  idleTimeout: 'IDLE_TIMEOUT',
  ticketInvalid: 'TICKET_INVALID',
  worldFull: 'WORLD_FULL',
} as const;
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/** WebSocket close codes in the application range (4000-4999), one per error code. */
export const CLOSE_CODES = {
  clientOutdated: 4400,
  unauthorized: 4401,
  sessionReplaced: 4402,
  kicked: 4403,
  idleTimeout: 4404,
  /** Missing, expired or already used admission ticket: go back to the queue. */
  ticketInvalid: 4405,
} as const;

/** Maps a close code received by the client to the error code (and so to an i18n key). */
export function errorCodeForClose(code: number): ErrorCode | undefined {
  switch (code) {
    case CLOSE_CODES.clientOutdated:
      return ERROR_CODES.clientOutdated;
    case CLOSE_CODES.unauthorized:
      return ERROR_CODES.unauthorized;
    case CLOSE_CODES.sessionReplaced:
      return ERROR_CODES.sessionReplaced;
    case CLOSE_CODES.kicked:
      return ERROR_CODES.kicked;
    case CLOSE_CODES.idleTimeout:
      return ERROR_CODES.idleTimeout;
    case CLOSE_CODES.ticketInvalid:
      return ERROR_CODES.ticketInvalid;
    default:
      return undefined;
  }
}

export const SUPPORTED_LOCALES = ['en', 'sv'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
